/**
 * RevenueIntelligenceCockpit — Cockpit structural rewrite of /revenue-intelligence.
 * Reuses revenueIntelligence selectors; no business logic changes.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import {
  computeJobEconomics, aggregateBySource, aggregateByRep,
  generateRevenueAlerts, computeRevenueSummary, type JobEconomics,
} from "@/data/revenueIntelligence";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitKpiStrip, CockpitTabs, CockpitFinancialTable, type CockpitColumn,
  CockpitSection, CockpitChipGroup, StatusDot,
} from "@/components/cockpit/CockpitShell";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const CURRENT_REP = "Mike Johnson";

function MarginCell({ margin }: { margin: number }) {
  const color = margin >= 0.30 ? "text-success" : margin >= 0.15 ? "text-foreground" : "text-destructive";
  const Icon = margin >= 0.30 ? ArrowUpRight : margin >= 0.15 ? Minus : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center justify-end gap-1 tabular-nums", color)}>
      <Icon className="h-3 w-3" />{pct(margin)}
    </span>
  );
}

export default function RevenueIntelligenceCockpit() {
  const { jobs } = useAppData();
  const fin$ = useFinancialPermissions();
  const navigate = useNavigate();
  const [tab, setTab] = useState("source");
  const [sort, setSort] = useState<"margin" | "profit" | "revenue">(fin$.viewMargin ? "margin" : "revenue");

  const scopedJobs = useMemo(
    () => fin$.scopedToOwn ? jobs.filter((j) => j.salesRep === CURRENT_REP) : jobs,
    [jobs, fin$.scopedToOwn],
  );
  const showMargin = fin$.viewMargin;
  const showInternalCost = fin$.viewInternalCost;
  const showRepLeaderboard = fin$.viewRepLeaderboard;

  const summary = useMemo(() => computeRevenueSummary(scopedJobs), [scopedJobs]);
  const econs = useMemo(() => scopedJobs.map(computeJobEconomics), [scopedJobs]);
  const sources = useMemo(() => aggregateBySource(scopedJobs), [scopedJobs]);
  const reps = useMemo(() => aggregateByRep(scopedJobs), [scopedJobs]);
  const alerts = useMemo(() => generateRevenueAlerts(scopedJobs), [scopedJobs]);

  const sortedEcons = useMemo(() => {
    const s = [...econs];
    if (sort === "margin") s.sort((a, b) => a.margin - b.margin);
    else if (sort === "profit") s.sort((a, b) => a.netProfit - b.netProfit);
    else s.sort((a, b) => b.contractValue - a.contractValue);
    return s;
  }, [econs, sort]);

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow={fin$.scopedToOwn ? "Financials · My Revenue" : "Financials · Revenue Intelligence"}
        title="Revenue Intelligence"
        right={
          <>
            <HeroMetric label="Revenue" value={fmt(summary.totalRevenue)} hero />
            {showMargin && <HeroDivider />}
            {showMargin && <HeroMetric label="Net Profit" value={fmt(summary.totalProfit)} tone="good" />}
            {showMargin && <HeroDivider />}
            {showMargin && <HeroMetric label="Avg Margin" value={pct(summary.avgMargin)} tone={summary.avgMargin < 0.2 ? "bad" : "good"} />}
          </>
        }
      />

      <CockpitKpiStrip
        items={[
          { label: "Marketing Spend", value: fmt(summary.totalMarketingCost) },
          { label: "At-Risk Revenue", value: fmt(summary.atRiskRevenue), tone: summary.atRiskRevenue > 0 ? "bad" : "default" },
          { label: "Active Alerts", value: alerts.length, tone: alerts.length > 0 ? "warn" : "good" },
          { label: "Total Jobs", value: scopedJobs.length },
        ]}
      />

      {alerts.length > 0 && (
        <CockpitSection eyebrow="Attention" title={`Revenue Alerts · ${alerts.length}`}>
          <div className="border-y border-border/60 divide-y divide-border/60">
            {alerts.map((a) => (
              <div key={a.id} className="px-4 py-3 flex items-start gap-3">
                <StatusDot tone={a.severity === "critical" ? "bad" : "warn"} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{a.message}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.suggestedAction}</p>
                </div>
                {a.jobIds.length > 0 && (
                  <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{a.jobIds.length} jobs</span>
                )}
              </div>
            ))}
          </div>
        </CockpitSection>
      )}

      <CockpitTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "source", label: "By Source", count: sources.length },
          ...(showRepLeaderboard ? [{ value: "rep", label: "By Rep", count: reps.length }] : []),
          { value: "jobs", label: fin$.scopedToOwn ? "My Jobs" : "All Jobs", count: econs.length },
        ]}
      />

      {tab === "source" && (
        <CockpitFinancialTable
          columns={[
            { key: "source", label: "Source", render: (s: typeof sources[0]) => <span className="font-medium">{s.source}</span> },
            { key: "jobs", label: "Jobs", numeric: true, render: (s) => s.totalJobs },
            { key: "rev", label: "Revenue", numeric: true, render: (s) => fmt(s.totalRevenue) },
            ...(showMargin ? [{ key: "profit", label: "Profit", numeric: true, render: (s: typeof sources[0]) => <span className={s.totalProfit < 0 ? "text-destructive" : ""}>{fmt(s.totalProfit)}</span> }] : []),
            ...(showMargin ? [{ key: "marg", label: "Avg Margin", numeric: true, render: (s: typeof sources[0]) => <MarginCell margin={s.avgMargin} /> }] : []),
            { key: "cac", label: "CAC", numeric: true, render: (s) => s.cac > 0 ? fmt(s.cac) : "—" },
            { key: "spend", label: "Lead Spend", numeric: true, render: (s) => s.totalLeadCost > 0 ? fmt(s.totalLeadCost) : "—" },
          ] as CockpitColumn<typeof sources[0]>[]}
          rows={sources}
          getKey={(s) => s.source}
        />
      )}

      {tab === "rep" && showRepLeaderboard && (
        <CockpitFinancialTable
          columns={[
            { key: "rep", label: "Rep", render: (r: typeof reps[0]) => <span className="font-medium">{r.rep}</span> },
            { key: "jobs", label: "Jobs", numeric: true, render: (r) => r.totalJobs },
            { key: "rev", label: "Revenue", numeric: true, render: (r) => fmt(r.totalRevenue) },
            { key: "profit", label: "Profit", numeric: true, render: (r) => <span className={r.totalProfit < 0 ? "text-destructive" : ""}>{fmt(r.totalProfit)}</span> },
            { key: "avg", label: "Avg Contract", numeric: true, render: (r) => fmt(r.avgContractValue) },
            { key: "marg", label: "Avg Margin", numeric: true, render: (r) => <MarginCell margin={r.avgMargin} /> },
          ] as CockpitColumn<typeof reps[0]>[]}
          rows={reps}
          getKey={(r) => r.rep}
        />
      )}

      {tab === "jobs" && (
        <>
          <div className="flex items-center justify-end gap-1.5 -mt-2">
            <CockpitChipGroup
              value={sort}
              onChange={(v) => setSort(v as typeof sort)}
              options={[
                ...(showMargin ? [{ value: "margin", label: "Low Margin" }] : []),
                ...(showMargin ? [{ value: "profit", label: "Low Profit" }] : []),
                { value: "revenue", label: "High Revenue" },
              ]}
            />
          </div>
          <CockpitFinancialTable
            columns={[
              { key: "id", label: "Job", render: (e: JobEconomics) => <span className="font-medium font-mono text-xs">{e.jobId}</span> },
              { key: "cust", label: "Customer", render: (e) => e.customerName },
              { key: "src", label: "Source", render: (e) => <span className="text-muted-foreground">{e.leadSource}</span> },
              { key: "rep", label: "Rep", render: (e) => <span className="text-muted-foreground">{e.salesRep}</span> },
              { key: "ctr", label: "Contract", numeric: true, render: (e) => fmt(e.contractValue) },
              ...(showInternalCost ? [{ key: "mat", label: "Material", numeric: true, render: (e: JobEconomics) => fmt(e.materialCost) }] : []),
              ...(showInternalCost ? [{ key: "ins", label: "Install", numeric: true, render: (e: JobEconomics) => fmt(e.installCost) }] : []),
              { key: "mkt", label: "Mkt", numeric: true, render: (e) => e.marketingCost > 0 ? fmt(e.marketingCost) : "—" },
              ...(showMargin ? [{ key: "prof", label: "Profit", numeric: true, render: (e: JobEconomics) => <span className={cn("font-medium", e.netProfit < 0 && "text-destructive")}>{fmt(e.netProfit)}</span> }] : []),
              ...(showMargin ? [{ key: "marg", label: "Margin", numeric: true, render: (e: JobEconomics) => <MarginCell margin={e.margin} /> }] : []),
            ] as CockpitColumn<JobEconomics>[]}
            rows={sortedEcons}
            getKey={(e) => e.jobId}
            onRowClick={(e) => navigate(`/jobs/${e.jobId}`)}
          />
        </>
      )}
    </CockpitPage>
  );
}
